import React, { useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { BookingModal } from '@/components/BookingModal';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const navLinks = [
    { label: 'Home', href: '#home', testId: 'nav-link-home' },
    { label: 'Departments', href: '#departments', testId: 'nav-link-departments' },
    { label: 'Lab Tests', href: '#tests', testId: 'nav-link-tests' },
    { label: 'BMI Calculator', href: '#bmi', testId: 'nav-link-bmi' },
    { label: 'View Bill', href: '#portal', testId: 'nav-link-portal' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <>
      <header
        data-testid="header-navbar"
        className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/85 glass-effect"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-20 items-center justify-between">
            {/* Logo & Brand */}
            <a
              href="#home"
              data-testid="brand-logo-link"
              onClick={(e) => scrollToSection(e, '#home')}
              className="flex items-center space-x-3"
            >
              <img 
                src="/logo.png" 
                alt="Ushakiran Medicos Logo" 
                className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-rose-600 font-['Outfit'] tracking-tight">
                  USHAKIRAN MEDICOS
                </span>
                <span className="text-xs text-slate-500 font-medium tracking-wide">Estd. 2009</span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={link.testId}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-sm font-medium text-slate-700 hover:text-rose-600 transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                data-testid="header-book-appointment-button"
                onClick={() => setShowBookingModal(true)}
                className="bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:shadow-lg hover:shadow-rose-500/50 transition-all duration-300"
              >
                <Phone className="w-4 h-4 mr-2" />
                Book Appointment
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="lg:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="mobile-hamburger-menu-button"
                    className="text-slate-700"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                  <div className="flex flex-col space-y-6 mt-8">
                    <div className="flex flex-col space-y-4">
                      {navLinks.map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          data-testid={link.testId}
                          onClick={(e) => scrollToSection(e, link.href)}
                          className="text-lg font-medium text-slate-700 hover:text-rose-600 transition-colors py-2"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                    <Button
                      data-testid="header-book-appointment-button-mobile"
                      onClick={() => {
                        setIsOpen(false);
                        setShowBookingModal(true);
                      }}
                      className="bg-gradient-to-r from-rose-600 to-pink-600 text-white w-full"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Book Appointment
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        bookingType="global"
      />
    </>
  );
};